import { Vue } from "vue-property-decorator";
import {
  HisInfoSelectDialog,
} from "../../his/dialogs";
import {
  AuthenticationDialog,
} from "../../authentication/dialogs";
import {
  ExtraDocumentSelectorDialog,
  ExtraPagePositionInputDialog,
} from "../dialogs";
import {
  AiPhraseDialog,
  AiSyntaxDialog,
  AiPreprocessDialog,
} from "../aiPhrase/dialogs";
import {
  ClipboardAdapter,
  FileViewerAdapter,
  ExternalScriptAdapter,
  ClaioAdapter,
  ClaioSchemaAdapter,
  MksTemplateAdapter,
  DynamicTemplateAdapter,
  ProRadDrawAdapter,
  SignAdapter,
} from "./adapters";
import { ClickChainAgent } from "./agents";
import {
  ImageFileUploaderDialog,
  SignDialog,
  PrintPdfPreviewDialog,
  UserDefinitionSelectDialog,
  PatientProfileEditDialog,
  ReferenceFileSelectDialog,
  ApplicationLaunchDialog,
  MksTemplateSelectDialog,
  DynamicTemplateSelectDialog,
  ValueConfirmDialog,
} from "./dialogs";
import {
  PageEditor,
  ExtraPageEditor,
  HisInformationEditor,
  UserDefinitionEditor,
  PatientProfileEditor,
  DocumentImageSwapper,
} from "./editors";
import {
  FuzzyDateSelectDialog,
  FuzzyTimeSelectDialog,
} from "./objects/dialogs";
import {
  DocumentValidator,
  DocumentPrinter,
  DocumentRegisterer,
} from "./operators";
import {
  TrimmingDialog,
} from "../../drawing/dialogs";

class ComponentsAccessor {
  constructor(protected readonly _vm: Vue) {
  }

  protected getComponent<T extends Vue>(name: string): T {
    return this._vm.$refs[name] as T;
  }
}

export class CommonComponentsAccessor extends ComponentsAccessor {
  constructor(vm: Vue) {
    super(vm);
  }

  get dialogs(): CommonDialogsAccessor {
    return new CommonDialogsAccessor(this._vm);
  }

  get editors(): CommonEditorsAccessor {
    return new CommonEditorsAccessor(this._vm);
  }

  get operators(): CommonOperatorsAccessor {
    return new CommonOperatorsAccessor(this._vm);
  }

  get adapters(): CommonAdaptersAccessor {
    return new CommonAdaptersAccessor(this._vm);
  }

  get agents(): CommonAgentsAccessor {
    return new CommonAgentsAccessor(this._vm);
  }
}

export class CommonDialogsAccessor extends ComponentsAccessor {
  constructor(vm: Vue) {
    super(vm);
  }

  get imageFileUploader(): ImageFileUploaderDialog {
    return this.getComponent<ImageFileUploaderDialog>("imageFileUploadDialog");
  }

  get sign(): SignDialog {
    return this.getComponent<SignDialog>("signDialog");
  }

  get date(): DateDialogsAccessor {
    return new DateDialogsAccessor(this._vm);
  }

  get time(): TimeDialogsAccessor {
    return new TimeDialogsAccessor(this._vm);
  }

  get printer(): PrinterDialogsAccessor {
    return new PrinterDialogsAccessor(this._vm);
  }

  get extraPage(): ExtraPageDialogsAccessor {
    return new ExtraPageDialogsAccessor(this._vm);
  }

  get hisInformation(): HisInformationDialogsAccessor {
    return new HisInformationDialogsAccessor(this._vm);
  }

  get authentication(): AuthenticationDialogsAccessor {
    return new AuthenticationDialogsAccessor(this._vm);
  }

  get userDefinition(): UserDefinitionDialogsAccessor {
    return new UserDefinitionDialogsAccessor(this._vm);
  }

  get patientProfile(): PatientProfileDialogsAccessor {
    return new PatientProfileDialogsAccessor(this._vm);
  }

  get referenceFile(): ReferenceFileDialogsAccessor {
    return new ReferenceFileDialogsAccessor(this._vm);
  }

  get application(): ApplicationDialogsAccessor {
    return new ApplicationDialogsAccessor(this._vm);
  }

  get mksTemplate(): MksTemplateDialogsAccessor {
    return new MksTemplateDialogsAccessor(this._vm);
  }

  get dynamicTemplate(): DynamicTemplateDialogsAccessor {
    return new DynamicTemplateDialogsAccessor(this._vm);
  }

  get valueConfirmation(): ValueConfirmDialogAccessor {
    return new ValueConfirmDialogAccessor(this._vm);
  }

  get aiPhrase(): AiPhraseDialog {
    return this.getComponent<AiPhraseDialog>("aiPhraseDialog");
  }

  get trimming(): TrimmingDialog {
    return this.getComponent<TrimmingDialog>("trimmingDialog");
  }

  get aiSyntax(): AiSyntaxDialog {
    return this.getComponent<AiSyntaxDialog>("aiSyntaxDialog");
  }

  get aiPreprocess(): AiPreprocessDialog {
    return this.getComponent<AiPreprocessDialog>("aiPreprocessDialog");
  }
}

export class DateDialogsAccessor extends ComponentsAccessor {
  constructor(vm: Vue) {
    super(vm);
  }

  get fuzzySelector(): FuzzyDateSelectDialog {
    return this.getComponent<FuzzyDateSelectDialog>("fuzzyDateSelectDialog");
  }
}

export class PrinterDialogsAccessor extends ComponentsAccessor {
  constructor(vm: Vue) {
    super(vm);
  }

  get preview(): PrintPdfPreviewDialog {
    return this.getComponent<PrintPdfPreviewDialog>("printPdfPreviewDialog");
  }
}

export class TimeDialogsAccessor extends ComponentsAccessor {
  constructor(vm: Vue) {
    super(vm);
  }

  get fuzzySelector(): FuzzyTimeSelectDialog {
    return this.getComponent<FuzzyTimeSelectDialog>("fuzzyTimeSelectDialog");
  }
}

export class HisInformationDialogsAccessor extends ComponentsAccessor {
  constructor(vm: Vue) {
    super(vm);
  }

  get selector(): HisInfoSelectDialog {
    return this.getComponent<HisInfoSelectDialog>("hisInformationSelectDialog");
  }
}

export class AuthenticationDialogsAccessor extends ComponentsAccessor {
  constructor(vm: Vue) {
    super(vm);
  }

  get reauthenticator(): AuthenticationDialog {
    return this.getComponent<AuthenticationDialog>("reauthenticationDialog");
  }
}

export class UserDefinitionDialogsAccessor extends ComponentsAccessor {
  constructor(vm: Vue) {
    super(vm);
  }

  get selector(): UserDefinitionSelectDialog {
    return this.getComponent<UserDefinitionSelectDialog>("userDefinitionSelectDialog");
  }
}

export class PatientProfileDialogsAccessor extends ComponentsAccessor {
  constructor(vm: Vue) {
    super(vm);
  }

  get editor(): PatientProfileEditDialog {
    return this.getComponent<PatientProfileEditDialog>("patientProfileEditDialog");
  }
}

export class ReferenceFileDialogsAccessor extends ComponentsAccessor {
  constructor(vm: Vue) {
    super(vm);
  }

  get selector(): ReferenceFileSelectDialog {
    return this.getComponent<ReferenceFileSelectDialog>("referenceFileSelectDialog");
  }
}

export class ExtraPageDialogsAccessor extends ComponentsAccessor {
  constructor(vm: Vue) {
    super(vm);
  }

  get documentSelector(): ExtraDocumentSelectorDialog {
    return this.getComponent<ExtraDocumentSelectorDialog>("extraDocumentSelectDialog");
  }

  get positionSelector(): ExtraPagePositionInputDialog {
    return this.getComponent<ExtraPagePositionInputDialog>("extraPagePositionInputDialog");
  }
}

export class ApplicationDialogsAccessor extends ComponentsAccessor {
  constructor(vm: Vue) {
    super(vm);
  }

  get launcher(): ApplicationLaunchDialog {
    return this.getComponent<ApplicationLaunchDialog>("applicationLaunchDialog");
  }
}

export class MksTemplateDialogsAccessor extends ComponentsAccessor {
  constructor(vm: Vue) {
    super(vm);
  }

  get templateSelector(): MksTemplateSelectDialog {
    return this.getComponent<MksTemplateSelectDialog>("mksTemplateSelectDialog");
  }
}

export class DynamicTemplateDialogsAccessor extends ComponentsAccessor {
  constructor(vm: Vue) {
    super(vm);
  }

  get templateSelector(): DynamicTemplateSelectDialog {
    return this.getComponent<DynamicTemplateSelectDialog>("dynamicTemplateSelectDialog");
  }
}

export class ValueConfirmDialogAccessor extends ComponentsAccessor {
  constructor(vm: Vue) {
    super(vm);
  }

  get preview(): ValueConfirmDialog {
    return this.getComponent<ValueConfirmDialog>("valueConfirmDialog");
  }
}

export class CommonEditorsAccessor extends ComponentsAccessor {
  constructor(vm: Vue) {
    super(vm);
  }

  get page(): PageEditor {
    return this.getComponent<PageEditor>("pageEditor");
  }

  get extraPage(): ExtraPageEditor {
    return this.getComponent<ExtraPageEditor>("extraPageEditor");
  }

  get hisInformation(): HisInformationEditor {
    return this.getComponent<HisInformationEditor>("hisInformationEditor");
  }

  get userDefinition(): UserDefinitionEditor {
    return this.getComponent<UserDefinitionEditor>("userDefinitionEditor");
  }

  get patientProfile(): PatientProfileEditor {
    return this.getComponent<PatientProfileEditor>("patientProfileEditor");
  }

  get imageSwapper(): DocumentImageSwapper {
    return this.getComponent<DocumentImageSwapper>("documentImageSwapper");
  }
}

export class CommonOperatorsAccessor extends ComponentsAccessor {
  constructor(vm: Vue) {
    super(vm);
  }

  get validator(): DocumentValidator {
    return this.getComponent<DocumentValidator>("documentValidator");
  }

  get printer(): DocumentPrinter {
    return this.getComponent<DocumentPrinter>("documentPrinter");
  }

  get registerer(): DocumentRegisterer {
    return this.getComponent<DocumentRegisterer>("documentRegisterer");
  }
}

export class CommonAdaptersAccessor extends ComponentsAccessor {
  constructor(vm: Vue) {
    super(vm);
  }

  get clipboard(): ClipboardAdapter {
    return this.getComponent<ClipboardAdapter>("clipboardAdapter");
  }

  get fileViewer(): FileViewerAdapter {
    return this.getComponent<FileViewerAdapter>("fileViewerAdapter");
  }

  get externalScript(): ExternalScriptAdapter {
    return this.getComponent<ExternalScriptAdapter>("externalScriptAdapter");
  }

  get claio(): ClaioAdapter {
    return this.getComponent<ClaioAdapter>("claioAdapter");
  }

  get claioSchema(): ClaioSchemaAdapter {
    return this.getComponent<ClaioSchemaAdapter>("claioSchemaAdapter");
  }

  get mksTemplate(): MksTemplateAdapter {
    return this.getComponent<MksTemplateAdapter>("mksTemplateAdapter");
  }

  get dynamicTemplate(): DynamicTemplateAdapter {
    return this.getComponent<DynamicTemplateAdapter>("dynamicTemplateAdapter");
  }

  get proRadDraw(): ProRadDrawAdapter {
    return this.getComponent<ProRadDrawAdapter>("proRadDrawAdapter");
  }
  
  get sign(): SignAdapter {
    return this.getComponent<SignAdapter>("signAdapter");
  }
}

export class CommonAgentsAccessor extends ComponentsAccessor {
  constructor(vm: Vue) {
    super(vm);
  }

  get clickChain(): ClickChainAgent {
    return this.getComponent<ClickChainAgent>("clickChainAgent");
  }
}
